import { useState, useEffect, useRef } from 'react';

// Redux & Action
import { useDispatch, useSelector } from 'react-redux';
import {
    registerCustomer,
    requestRegisterOtp,
    resendRegisterOtp,
    verifyRegisterOtp
} from "@core/redux/actions/auth/index";
import { setStatusSpinner } from "@core/redux/actions/app/spinner";
import { setDataAlert } from "@core/redux/actions/app/alert";

// Component
// import RegisterMode from "./ModeMore/index";
import RegisterMode from "./ModeSingle/index";
import OtpVerifyApp from "./OtpVerify";

const OTP_RESEND_COOLDOWN = 60; // server กันขอรหัสถี่กว่านี้ (SmsOtpResendCooldown) — ตอบ code 93
const MAX_RESEND = 3;   // ส่งซ้ำได้ 3 ครั้งต่อ ref
const MAX_WRONG = 3;    // กรอกผิดครบ 3 ครั้ง เบอร์โดนระงับ 1 ชม. (API ไม่ส่งจำนวนที่เหลือมา ต้องนับเอง)
const RATE_LIMIT_DELAY = 1200;  // rate limit 1 req/วินาที ต่อ token/IP — เว้นระยะก่อนยิงเส้นถัดไป
const MAX_CHAIN_HOPS = 3;       // เพดานการต่อเส้นอัตโนมัติต่อการกดสมัคร 1 ครั้ง

// OTP ถูกต้องแล้ว แต่ข้อมูลสมัครไม่ผ่าน — server เรียก RevertVerified() คืนสิทธิ์ให้ ref เดิมยิงซ้ำได้
// แต่ฝั่งนี้เลือกพากลับหน้าฟอร์มแล้วเริ่มขอรหัสใหม่ทั้งชุด (resetOtpFlow) ไม่ได้ใช้ ref เดิมต่อ
const KEEP_OTP_CODES = [14, 51, 67, 68, 37];
// code ที่ ref ใช้ไม่ได้อีกแล้ว → กลับไปเริ่มขอ OTP ใหม่
const DEAD_REF_CODES = [89, 97];

export default function RegisterApp(props) {
    const dispatch = useDispatch();
    const [bankList, setBankList] = useState([]);
    const [showAccName, setShowAccName] = useState(false);
    const dataSetting = useSelector(state => state.reduxAppDataSetting.result);
    const agentSettingBank = dataSetting?.agent_setting_bank;
    const [selectBank, setSelectBank] = useState(null);

    // ---- OTP ----
    const [otpInfo, setOtpInfo] = useState(null);       // { ref, phone, expireAt, lastSentAt }
    const [showOtp, setShowOtp] = useState(false);
    const [resendCount, setResendCount] = useState(0);
    const [wrongCount, setWrongCount] = useState(0);
    const [clearSignal, setClearSignal] = useState(0);
    const [formData, setFormData] = useState(null);
    // ยิง register-otp/request เป็นเส้นแรกเสมอ ไม่ต้องเดาจาก setting ที่อาจค้าง cache
    // null = ยังไม่รู้ · false = server ตอบ 95 มาแล้ว (agent ปิด OTP) → รอบต่อไปข้ามไป register-short เลย
    const [otpSupported, setOtpSupported] = useState(null);
    // เวลาที่ขอรหัสใหม่ได้ (unix seconds) — backend กัน 60 วิ นับจาก SMS ใบล่าสุดของเบอร์นั้น
    // ค่านี้ต้องรอดจาก resetOtpFlow เพราะ cooldown ผูกกับเบอร์ที่ server ไม่ใช่รอบการกรอก
    const [cooldownUntil, setCooldownUntil] = useState(0);
    const [tick, setTick] = useState(() => Math.floor(Date.now() / 1000));

    const nowInSeconds = () => Math.floor(Date.now() / 1000);
    const otpStillValid = otpInfo !== null && otpInfo.expireAt > nowInSeconds();

    // ยิงเส้นถัดไปแบบเว้นระยะกัน rate limit — คา spinner ไว้ระหว่างรอ จะได้ไม่มีจังหวะว่างให้กดซ้ำ
    const chainTimerRef = useRef(null);
    const chainHopsRef = useRef(0);

    const chainRequest = (run) => {
        // กัน ping-pong ถ้า agent สลับค่า customer_vertify_otp กลางคัน (95 ↔ 94 วนไม่จบ)
        if (chainHopsRef.current >= MAX_CHAIN_HOPS) {
            dispatch(setStatusSpinner({ status: false, textProcess: null }));
            dispatch(setDataAlert({
                type: "warning",
                title: "แจ้งเตือน",
                text: "เริ่มขั้นตอนสมัครไม่สำเร็จ กรุณาลองใหม่",
                action: true,
                redirect: "",
                show: true,
                buttonText: "ตกลง"
            }));
            return;
        }

        chainHopsRef.current += 1;
        dispatch(setStatusSpinner({ status: true, textProcess: "ระบบกำลังตรวจสอบข้อมูล กรุณารอสักครู่..." }));
        chainTimerRef.current = setTimeout(run, RATE_LIMIT_DELAY);
    };

    useEffect(() => {
        return () => {
            if (chainTimerRef.current !== null) {
                clearTimeout(chainTimerRef.current);
                dispatch(setStatusSpinner({ status: false, textProcess: null }));
            }
        };
    }, [dispatch]);

    // เดินนาฬิกาเฉพาะตอนที่ยังติด cooldown อยู่ — พ้นแล้วหยุดเอง ไม่ปล่อย interval ค้าง
    const cooldownLeft = Math.max(0, cooldownUntil - tick);

    useEffect(() => {
        if (cooldownUntil === 0) {
            return undefined;
        }

        const timer = setInterval(() => {
            const now = Math.floor(Date.now() / 1000);
            setTick(now);

            // พ้น cooldown แล้วล้างค่าทิ้ง เพื่อให้ effect นี้ cleanup interval ของตัวเอง
            // ไม่งั้นนาฬิกาเดินค้างตลอดอายุ modal แล้ว re-render ฟอร์มสมัครทุกวินาทีโดยเปล่าประโยชน์
            if (now >= cooldownUntil) {
                setCooldownUntil(0);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [cooldownUntil]);

    // บอก modal ว่ากำลังอยู่ในขั้นตอน OTP เพื่อให้ถามยืนยันก่อนปิด
    // เก็บ callback ไว้ใน ref เพราะ props เปลี่ยน object ทุก render
    const onOtpProcessRef = useRef(props.onOtpProcess);
    onOtpProcessRef.current = props.onOtpProcess;

    useEffect(() => {
        if (onOtpProcessRef.current) {
            onOtpProcessRef.current(showOtp);
        }
    }, [showOtp]);

    // ออกจากหน้าจอนี้ทุกกรณี (ปิด modal / สลับไปแท็บเข้าสู่ระบบ) ต้องปลดธงให้ modal ปิดได้ตามปกติ
    useEffect(() => {
        return () => {
            if (onOtpProcessRef.current) {
                onOtpProcessRef.current(false);
            }
        };
    }, []);

    // สมัครสมาชิกสำเร็จ = จบขั้นตอน OTP แล้ว ต้องปลดธงก่อนสั่งปิด modal
    // ไม่งั้น AuthApp จะนึกว่ายังค้างอยู่หน้ากรอกรหัส แล้วเด้งถามยืนยันออกทั้งที่สมัครผ่านไปแล้ว
    const closeAfterRegister = () => {
        if (onOtpProcessRef.current) {
            onOtpProcessRef.current(false);
        }

        setShowOtp(false);

        if (props.close) {
            props.close();
        }
    };

    // นับถอยหลังจาก expire_in โดยอิงนาฬิกาเครื่องผู้ใช้ กันเวลาเครื่องกับ server ไม่ตรงกัน
    const resolveExpireAt = (result) => {
        if (result?.expire_in) {
            return nowInSeconds() + Number(result.expire_in);
        }

        return result?.expire_at || 0;
    };

    // ทุกครั้งที่ SMS ถูกส่งจริง เริ่มนับ cooldown 60 วิ ให้ตรงกับฝั่ง server
    const markSmsSent = () => {
        setCooldownUntil(nowInSeconds() + OTP_RESEND_COOLDOWN);
        setTick(nowInSeconds());
    };

    // กลับไปหน้าฟอร์ม พร้อมทิ้ง ref เดิม (ต้องขอ OTP ใหม่)
    const resetOtp = () => {
        setOtpInfo(null);
        setShowOtp(false);
        setResendCount(0);
        setWrongCount(0);
    };

    // ออกจากขั้นตอนยืนยัน OTP ไม่ว่าด้วยเหตุใด = เริ่มใหม่ทั้งกระบวนการ
    // ทิ้ง ref เดิม ตัวนับ และผลที่จำไว้จากรอบก่อนให้หมด กดสมัครครั้งถัดไปต้องขอรหัสใหม่เสมอ
    const resetOtpFlow = () => {
        if (chainTimerRef.current !== null) {
            clearTimeout(chainTimerRef.current);
            chainTimerRef.current = null;
            dispatch(setStatusSpinner({ status: false, textProcess: null }));
        }

        chainHopsRef.current = 0;
        resetOtp();
        setFormData(null);
        setOtpSupported(null);
    };

    // ปุ่มย้อนกลับของเบราว์เซอร์ระหว่างค้างอยู่หน้า OTP — modal ไม่ได้ผูกกับ route จึงไม่ปิดเอง
    const resetOtpFlowRef = useRef(null);
    resetOtpFlowRef.current = resetOtpFlow;

    useEffect(() => {
        const onPopState = () => {
            if (resetOtpFlowRef.current !== null) {
                resetOtpFlowRef.current();
            }
        };

        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, []);

    const openOtpScreen = (result, phoneFallback) => {
        setOtpInfo({
            ref: result?.ref,
            phone: result?.phone || phoneFallback,
            expireAt: resolveExpireAt(result),
            lastSentAt: nowInSeconds()
        });
        markSmsSent();
        setResendCount(0);
        setWrongCount(0);
        setClearSignal(value => value + 1);
        setShowOtp(true);
    };

    // สมัครแบบไม่ผ่าน OTP — retried กันเคสโดน rate limit ตอนต่อท้าย request ที่เพิ่งตอบ 95
    const registerShort = (data, retried) => {
        dispatch(registerCustomer(
            data,
            closeAfterRegister,
            selectBank,
            setShowAccName,
            () => {
                // 94 = agent เปิด OTP แล้ว → ย้อนกลับไปเส้น OTP
                setOtpSupported(null);
                chainRequest(() => startOtp(data));
            },
            (statusCode) => {
                if (statusCode === 429 && !retried) {
                    chainRequest(() => registerShort(data, true));
                    return true;
                }

                return false;
            }
        ));
    };

    const startOtp = (data) => {
        dispatch(requestRegisterOtp(
            data,
            selectBank,
            (result) => {
                setOtpSupported(true);
                openOtpScreen(result, data.phone);
            },
            (statusCode) => {
                // 95 = agent ไม่ได้เปิด OTP → ถอยไปใช้เส้นสมัครปกติเงียบ ๆ (ยังไม่มี SMS ถูกส่ง)
                if (statusCode === 95) {
                    setOtpSupported(false);
                    chainRequest(() => registerShort(data, false));
                    return true;
                }

                // 68 = ธนาคารยืนยันชื่อบัญชีไม่ได้ → เปิดช่องให้กรอกชื่อบัญชีเอง แล้วกดสมัครใหม่
                // เดิม code นี้โผล่ตอน vertify คือหลัง SMS ออกไปแล้ว ตอนนี้ API ตรวจให้ตั้งแต่ก่อนส่ง
                if (statusCode === 68) {
                    setShowAccName(true);
                }

                // 66 เบอร์ผิด/ซ้ำ · 14 username ซ้ำ · 51 บัญชีซ้ำ · 93 ขอถี่ · 97 ถูกระงับ · 13 ส่ง SMS ไม่ออก
                // → อยู่หน้าฟอร์ม แก้ข้อมูลแล้วกดสมัครใหม่ได้ (alert ขึ้นให้จาก alertByCode)
                return false;
            }
        ));
    };

    const onSubmit = (data) => {
        setFormData(data);
        chainHopsRef.current = 0;

        // เคยเจอ 95 มาแล้วในรอบนี้ = agent ปิด OTP อยู่ ไม่ต้องยิง request ให้เสียเที่ยวอีก
        if (otpSupported === false) {
            registerShort(data, false);
            return;
        }

        // ทุกครั้งที่กดสมัคร = เริ่มขั้นตอน OTP ใหม่ ไม่มีการใช้ ref เดิมต่อ
        setOtpInfo(null);
        startOtp(data);
    };

    const onSubmitOtp = (otp) => {
        dispatch(verifyRegisterOtp(
            formData,
            closeAfterRegister,
            selectBank,
            setShowAccName,
            { ref: otpInfo?.ref, otp },
            (statusCode) => {
                // 92 = OTP ผิด → อยู่หน้าเดิม ให้กรอกใหม่ (นับโควตาเอง ครั้งที่ 3 API จะตอบ 97 แทน)
                if (statusCode === 92) {
                    setWrongCount(value => value + 1);
                    setClearSignal(value => value + 1);
                    return false;
                }

                // 90 = OTP หมดอายุ → อยู่หน้าเดิม ให้กดส่งรหัสใหม่ (บังคับให้ตัวนับขึ้นหมดอายุทันที)
                if (statusCode === 90) {
                    setOtpInfo(current => ({ ...current, expireAt: nowInSeconds() }));
                    setClearSignal(value => value + 1);
                    return false;
                }

                // 89 ไม่พบ ref / ใช้ไปแล้ว · 97 กรอกผิดครบ 3 ครั้ง เบอร์โดนระงับ → กลับหน้าฟอร์ม
                if (DEAD_REF_CODES.includes(statusCode)) {
                    if (statusCode === 97) {
                        setWrongCount(MAX_WRONG);
                    }
                    resetOtpFlow();
                    return false;
                }

                // ข้อมูลสมัครสมาชิกมีปัญหา (username ซ้ำ / บัญชีธนาคารไม่ผ่าน ฯลฯ)
                // → ออกจากหน้า OTP ไปแก้ที่ฟอร์ม = หลุดจากขั้นตอนแล้ว ต้องขอรหัสใหม่
                if (KEEP_OTP_CODES.includes(statusCode)) {
                    resetOtpFlow();
                    return false;
                }

                // 66 / 1 / 17 / 429 → อยู่หน้าเดิม ล้างช่องกรอก
                setClearSignal(value => value + 1);
                return false;
            }
        ));
    };

    const onResend = () => {
        dispatch(resendRegisterOtp(
            formData?.phone,
            otpInfo?.ref,
            (result) => {
                // resend ใช้รหัสเดิม แค่ต่ออายุ — ไม่ล้างช่องกรอก และไม่ล้างโควตากรอกผิด
                setOtpInfo(current => ({
                    ...current,
                    phone: result?.phone || current?.phone,
                    expireAt: resolveExpireAt(result),
                    lastSentAt: nowInSeconds()
                }));
                setResendCount(value => value + 1);
                markSmsSent();
            },
            (statusCode) => {
                // 89 ไม่พบ ref · 97 เบอร์ถูกระงับ → กลับหน้าฟอร์ม
                if (DEAD_REF_CODES.includes(statusCode)) {
                    resetOtpFlow();
                    return false;
                }

                // 96 = ส่งซ้ำครบ 3 ครั้งแล้ว → ปิดปุ่มส่งซ้ำ ถ้ารหัสหมดอายุไปด้วยก็กลับหน้าฟอร์ม
                if (statusCode === 96) {
                    setResendCount(MAX_RESEND);

                    if (!otpStillValid) {
                        resetOtpFlow();
                    }
                    return false;
                }

                // 93 ยังไม่พ้น cooldown 60 วิ · 13 ส่ง SMS ไม่ออก → อยู่หน้าเดิม
                return false;
            }
        ));
    };

    // รหัสหมดอายุและส่งซ้ำครบ 3 ครั้งแล้ว — พากลับหน้าฟอร์มเพื่อเริ่มขอรหัสใหม่
    const onExhausted = () => {
        resetOtpFlow();
        dispatch(setDataAlert({
            type: "warning",
            title: "แจ้งเตือน",
            text: "รหัสหมดอายุและส่งซ้ำครบแล้ว กรุณาสมัครใหม่อีกครั้ง",
            action: true,
            redirect: "",
            show: true,
            buttonText: "ตกลง"
        }));
    };

    useEffect(() => {
        if (agentSettingBank) {
            setBankList(agentSettingBank);
        }
    }, [agentSettingBank]);

    if (dataSetting === null || !(bankList?.length > 0)) {
        return null;
    }

    return (
        <>
            {/* ซ่อนฟอร์มไว้แทนการถอดออก เพื่อคงค่าที่กรอกไว้ตอนสลับไปหน้า OTP */}
            <div style={{ display: showOtp ? "none" : "block" }}>
                <RegisterMode
                    {...props}
                    bankList={bankList}
                    onSubmit={onSubmit}
                    showAccName={showAccName}
                    selectBank={selectBank}
                    setSelectBank={setSelectBank}
                    otpCooldownLeft={cooldownLeft}
                />
            </div>

            {
                showOtp && (
                    <OtpVerifyApp
                        phone={otpInfo?.phone}
                        refCode={otpInfo?.ref}
                        expireAt={otpInfo?.expireAt}
                        lastSentAt={otpInfo?.lastSentAt}
                        resendCount={resendCount}
                        maxResend={MAX_RESEND}
                        wrongCount={wrongCount}
                        maxWrong={MAX_WRONG}
                        clearSignal={clearSignal}
                        onSubmitOtp={onSubmitOtp}
                        onResend={onResend}
                        onExhausted={onExhausted}
                    />
                )
            }
        </>
    );
}
